/**
 * Auto-análise PNL (Bandler). Análise global + expansão on-demand por área.
 */
import { useState } from "react";
import { Brain, Loader2, Wand2, HeartHandshake, TrendingDown, TrendingUp, Target, Zap, ChevronDown, ChevronUp } from "lucide-react";
import { myApi } from "../../api";

export default function RodaAnalise({ assessment, analise, loading, onGenerate, onRefresh, onError }) {
  if (!analise) {
    return (
      <div
        className="bg-white rounded-2xl border border-[#E5E7EB] p-6 flex flex-col items-start gap-3 shadow-sm"
        data-testid="coach-analise-empty"
      >
        <div className="flex items-center gap-2">
          <Brain size={20} className="text-[#0F1829]" />
          <h2 className="text-lg font-semibold text-[#0F1829]">
            Auto-análise PNL (Bandler)
          </h2>
        </div>
        <p className="text-sm text-[#374151]">
          Segundo a corrente do Dr. Richard Bandler: identificamos valores
          (atraentes/repelentes), crenças limitadoras e potenciadoras,
          objetivos e metaprogramas — e o impacto que têm na tua vida.
        </p>
        <button
          onClick={onGenerate}
          disabled={loading}
          data-testid="coach-btn-generate-analise"
          className="inline-flex items-center gap-2 bg-[#0F1829] text-white text-sm font-medium px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
          {loading ? "A analisar…" : "Fazer auto-análise PNL"}
        </button>
      </div>
    );
  }
  return (
    <>
      <AnaliseBody analise={analise} testidPrefix="coach-analise" />
      <AreaDrillDown
        assessment={assessment}
        onRefresh={onRefresh}
        onError={onError}
      />
    </>
  );
}

function AnaliseBody({ analise, testidPrefix, compact = false }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm ${compact ? "" : "space-y-5"}`}
      data-testid={testidPrefix}
    >
      {!compact && (
        <div className="flex items-center gap-2 mb-1">
          <Brain size={20} className="text-[#0F1829]" />
          <h2 className="text-lg font-semibold text-[#0F1829]">
            Auto-análise PNL (Bandler)
            {analise.areaFoco && (
              <span className="ml-2 text-sm font-normal text-[#4A5E2E]">
                · foco em {analise.areaFoco}
              </span>
            )}
          </h2>
        </div>
      )}

      <ListPair
        titleLeft="Valores atraentes"
        titleRight="Valores repelentes"
        left={analise.valoresAtraentes}
        right={analise.valoresRepelentes}
        iconLeft={HeartHandshake}
        iconRight={TrendingDown}
        colorLeft="#4A5E2E"
        colorRight="#B91C1C"
      />

      <ListCards
        title="Crenças limitadoras"
        icon={TrendingDown}
        accent="#B91C1C"
        items={analise.crencasLimitadoras}
        keyField="crenca"
        subField="impacto"
      />

      <ListCards
        title="Crenças potenciadoras"
        icon={TrendingUp}
        accent="#4A5E2E"
        items={analise.crencasPotenciadoras}
        keyField="crenca"
        subField="impacto"
      />

      {analise.objetivos?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#0F1829] mb-2 flex items-center gap-1">
            <Target size={16} className="text-[#4A5E2E]" /> Objetivos identificados
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {analise.objetivos.map((o, i) => (
              <li
                key={i}
                className="rounded-md border border-[#E5E7EB] bg-[#FBFAF7] p-3 text-sm"
                data-testid={`${testidPrefix}-objetivo-${i}`}
              >
                <div className="font-medium text-[#0F1829]">{o.objetivo}</div>
                <div className="text-xs text-[#6B7280] mt-1">
                  {o.area && <span>{o.area}</span>}
                  {o.prazo && <span> · prazo {o.prazo}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {analise.metaprogramas?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#0F1829] mb-2 flex items-center gap-1">
            <Zap size={16} className="text-[#978D78]" /> Metaprogramas &amp; impacto
          </h3>
          <ul className="space-y-2">
            {analise.metaprogramas.map((m, i) => (
              <li
                key={i}
                className="rounded-md border border-[#E5E7EB] bg-white p-3"
                data-testid={`${testidPrefix}-meta-${i}`}
              >
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  <span className="text-sm font-semibold text-[#0F1829]">
                    {m.metaprograma}
                  </span>
                  {m.valor && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#0F1829] text-white">
                      {m.valor}
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#374151]">{m.impacto}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {analise.sinteseBandler && (
        <div className="mt-2 rounded-xl bg-[#0F1829] text-white p-4">
          <div className="text-xs uppercase tracking-widest font-semibold text-white/70 mb-1">
            Síntese na linguagem Bandler
          </div>
          <p className="text-sm leading-relaxed">{analise.sinteseBandler}</p>
        </div>
      )}
    </div>
  );
}

function ListPair({ titleLeft, titleRight, left, right, iconLeft: IL, iconRight: IR, colorLeft, colorRight }) {
  if ((!left || !left.length) && (!right || !right.length)) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <MiniList title={titleLeft} icon={IL} accent={colorLeft} items={left} />
      <MiniList title={titleRight} icon={IR} accent={colorRight} items={right} />
    </div>
  );
}

function MiniList({ title, icon: Icon, accent, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-[#FBFAF7] p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: accent }} />
        <span className="text-sm font-semibold text-[#0F1829]">{title}</span>
      </div>
      <ul className="text-sm space-y-1">
        {items.map((v) => (
          <li key={v} className="flex gap-2">
            <span style={{ color: accent }}>▸</span>
            <span className="text-[#374151]">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ListCards({ title, icon: Icon, accent, items, keyField, subField }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-[#0F1829] mb-2 flex items-center gap-1">
        <Icon size={16} style={{ color: accent }} /> {title}
      </h3>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li
            key={i}
            className="rounded-md border-l-2 pl-3 py-1 bg-white"
            style={{ borderColor: accent }}
          >
            <div className="text-sm font-medium text-[#0F1829]">{it[keyField]}</div>
            {it[subField] && (
              <div className="text-sm text-[#374151] mt-0.5">{it[subField]}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Expansão por área ──────────────────────────────────────────────
function AreaDrillDown({ assessment, onRefresh, onError }) {
  const [openArea, setOpenArea] = useState(null);
  const [loadingArea, setLoadingArea] = useState(null);
  const [byArea, setByArea] = useState(assessment.analise_area || {});

  const areas = Object.keys(assessment.scores || {});

  const load = async (area) => {
    if (byArea[area]) {
      setOpenArea(openArea === area ? null : area);
      return;
    }
    setLoadingArea(area);
    try {
      const r = await myApi.coachRodaAnaliseArea(assessment.id, area);
      const next = { ...byArea, [area]: r.analise };
      setByArea(next);
      setOpenArea(area);
      onRefresh({ ...assessment, analise_area: next });
    } catch (e) {
      onError(e.message);
    } finally {
      setLoadingArea(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="coach-drilldown">
      <h3 className="text-base font-semibold text-[#0F1829] mb-1">
        Análise focada por área
      </h3>
      <p className="text-sm text-[#6B7280] mb-3">
        Queres aprofundar uma área específica? Escolhe e recebes uma análise
        PNL exclusiva desse domínio.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {areas.map((a) => (
          <button
            key={a}
            onClick={() => load(a)}
            data-testid={`coach-drill-btn-${a.replace(/\s/g, "-")}`}
            className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border transition ${
              openArea === a
                ? "bg-[#0F1829] text-white border-[#0F1829]"
                : "bg-white text-[#0F1829] border-[#E5E7EB] hover:bg-[#F3F4F6]"
            }`}
          >
            {loadingArea === a ? (
              <Loader2 size={12} className="animate-spin" />
            ) : byArea[a] ? (
              openArea === a ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            ) : (
              <Wand2 size={12} />
            )}
            {a}
          </button>
        ))}
      </div>
      {openArea && byArea[openArea] && (
        <AnaliseBody
          analise={byArea[openArea]}
          testidPrefix={`coach-analise-area-${openArea.replace(/\s/g, "-")}`}
          compact
        />
      )}
    </div>
  );
}
